import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { JobCommentsThread } from '../components/job/JobCommentsThread'
import { supabase } from '../lib/supabase'

interface JobNotesSummary {
  id: string
  job_number: string
  title: string
  notes?: string | null
}

export function JobNotes() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [job, setJob] = useState<JobNotesSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fromState =
    location.state && typeof location.state === 'object' && 'from' in location.state
      ? String((location.state as { from?: unknown }).from || '')
      : ''

  useEffect(() => {
    if (!id) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const { data, error: fetchError } = await supabase
          .from('jobs')
          .select('id, job_number, title, notes')
          .eq('id', id)
          .single()
        if (fetchError) throw fetchError
        if (!cancelled) setJob(data as JobNotesSummary)
      } catch (e) {
        if (!cancelled) {
          setJob(null)
          setError(e instanceof Error ? e.message : 'Failed to load job notes')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [id])

  const goBack = () => {
    // Prefer history so Job Details keeps its original return state
    if (window.history.length > 1) {
      navigate(-1)
      return
    }
    if (fromState) {
      navigate(fromState)
      return
    }
    if (id) navigate(`/jobs/details/${id}`)
    else navigate('/jobs')
  }

  if (loading) {
    return (
      <MobilePage>
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </MobilePage>
    )
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title="Job Notes"
        subtitle={job ? `#${job.job_number} · ${job.title}` : undefined}
        onBack={goBack}
        backLabel="Back to job"
        sticky
      />

      {error || !job || !id ? (
        <div className={`${mobile.surface} p-4`}>
          <p className="text-sm text-gray-500">{error || 'Job not found.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          <section className={`${mobile.surface} p-4`}>
            <h2 className="text-sm font-semibold text-gray-900 mb-2">Scheduled notes</h2>
            {job.notes ? (
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{job.notes}</p>
            ) : (
              <p className="text-sm text-gray-500">No scheduled notes on this job.</p>
            )}
          </section>
          <section className={`${mobile.surface} p-4`}>
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Add a note</h2>
            <JobCommentsThread jobId={id} autoFocus />
          </section>
        </div>
      )}
    </MobilePage>
  )
}
